import { useEffect, useState } from "react";
import { Truck, Wrench, Gauge } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Empty } from "@/components/Common";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LevelBadge, DueCell } from "@/components/fleet/fleetConst";

const ICON = { vehicle: Truck, maintenance: Wrench, equipment: Gauge };

const Kpi = ({ label, value, tone, testId }) => (
  <div className={`border-l-4 bg-white px-5 py-4 ${tone}`} data-testid={testId}>
    <div className="micro-label">{label}</div>
    <div className="mt-1 font-display text-3xl font-extrabold">{value}</div>
  </div>
);

export const DeadlinesTab = ({ refreshKey, onOpenVehicle }) => {
  const { nameOf, inCompany } = useData();
  const [horizon, setHorizon] = useState("30");
  const [raw, setRows] = useState([]);
  const rows = raw.filter((r) => inCompany(r.company_id));
  useEffect(() => { api.get("/fleet/deadlines", { params: { horizon } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e))); }, [horizon, refreshKey]);
  const count = (lv) => rows.filter((r) => lv.includes(r.level)).length;
  return (
    <div data-testid="deadlines-tab">
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Kpi label="Scadute o in scadenza oggi" value={count(["OVERDUE", "TODAY"])} tone="border-red-600" testId="kpi-overdue" />
        <Kpi label="Entro 7 giorni" value={count(["D7"])} tone="border-amber-500" testId="kpi-d7" />
        <Kpi label="Entro 30 giorni" value={count(["D30"])} tone="border-yellow-400" testId="kpi-d30" />
      </div>
      <div className="mb-3 flex items-center gap-3">
        <Select value={horizon} onValueChange={setHorizon}>
          <SelectTrigger className="w-56 bg-white" data-testid="deadlines-horizon"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["30", "60", "90", "180"].map((h) => <SelectItem key={h} value={h}>Prossimi {h} giorni</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">Le manutenzioni a km si attivano a 2.000 km (avviso) e 500 km (urgente) dalla soglia.</span>
      </div>
      {rows.length === 0 ? <Empty text="Nessuna scadenza nel periodo" testId="deadlines-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Stato</TableHead><TableHead>Bene</TableHead><TableHead>Società</TableHead><TableHead>Scadenza</TableHead><TableHead>Data / km</TableHead><TableHead className="text-right">Mancano</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((d) => {
                const Icon = ICON[d.entity];
                return (
                  <TableRow key={d.key} className={d.vehicle_id ? "cursor-pointer" : ""} onClick={() => d.vehicle_id && onOpenVehicle(d.vehicle_id)} data-testid={`deadline-row-${d.entity_id}`}>
                    <TableCell><LevelBadge level={d.level} /></TableCell>
                    <TableCell className="text-sm"><span className="flex items-center gap-2"><Icon className="h-4 w-4 text-muted-foreground" /><span className="font-mono font-semibold">{d.ref}</span><span className="text-muted-foreground">{d.ref_detail}</span></span></TableCell>
                    <TableCell className="text-xs">{nameOf("companies", d.company_id, "code")}</TableCell>
                    <TableCell className="text-sm">{d.label}</TableCell>
                    <TableCell><DueCell date={d.due_date} level={d.level} />{d.due_km ? <span className="ml-2 font-mono text-xs">{Math.round(d.due_km).toLocaleString("it-IT")} km</span> : null}</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {d.days_left != null && (d.days_left < 0 ? `scaduto da ${-d.days_left} gg` : `${d.days_left} gg`)}
                      {d.km_left != null && <div>{Math.round(d.km_left).toLocaleString("it-IT")} km</div>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
